import type { CSSProperties, ReactNode } from 'react';

type ScopeOption = {
  id: number;
  name: string;
};

export function formatCommunityScopesLabel(
  scopes: Array<{ name?: string | null }> | null | undefined
): string {
  if (!scopes?.length) return '';
  return scopes
    .map((s) => String(s.name ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

export function ScopeCheckboxGroup({
  options,
  selectedIds,
  onChange,
  disabled,
  label = 'Scope',
}: {
  options: ScopeOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  label?: string;
}): ReactNode {
  const selected = new Set(selectedIds);

  function toggle(id: number, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    onChange([...next].sort((a, b) => a - b));
  }

  const fieldStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const listStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 16px',
  };

  const itemStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.65 : 1,
  };

  return (
    <div style={fieldStyle}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
      {!options.length ? (
        <span style={{ fontSize: 13, color: 'var(--color-muted, #64748b)' }}>No scopes available.</span>
      ) : (
        <div style={listStyle} role="group" aria-label={label}>
          {options.map((opt) => (
            <label key={opt.id} style={itemStyle}>
              <input
                type="checkbox"
                checked={selected.has(opt.id)}
                disabled={disabled}
                onChange={(e) => toggle(opt.id, e.target.checked)}
              />
              {opt.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
